/**
 * ScrollableTabs Component
 *
 * A horizontal scrollable tabs component built with Material-UI that supports:
 * - Smooth scrolling with mouse wheel and trackpad
 * - Navigation buttons for left/right scrolling
 * - Optional fixed tab that stays pinned to the right
 * - Active tab highlighting with custom styling
 * - Responsive design with dynamic scroll amounts
 *
 * @author Rollo Ship Manager Frontend Team
 * @version 1.0.0
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'relative',
    display: 'flex',
    alignItems: 'stretch',
    width: '100%',
    minHeight: 48,
  },
  tabsContainer: {
    overflow: 'hidden',
    flex: 1,
    position: 'relative',
    borderBottom: `1px solid #E1E1E1`,
  },
  tabsWrapper: {
    display: 'flex',
    transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    minHeight: 48,
    alignItems: 'stretch',
  },
  tab: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 15px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    minWidth: 'auto',
    borderBottom: '2px solid transparent',
    fontSize: '14px',
    fontWeight: 400,
    color: '#9E9E9E',
    transition: 'color 0.2s ease-in-out, border-bottom-color 0.2s ease-in-out',
    backgroundColor: 'transparent',
    position: 'relative',
    '&:hover': {
      color: '#6C13F9',
    },
    '&:not(:last-child)': {
      marginRight: '0px',
    },
  },
  activeTab: {
    color: '#6C13F9',
    borderBottomColor: '#6C13F9 !important',
    borderBottomWidth: '2px !important',
    fontWeight: 500,
  },
  fixedTab: {
    flexShrink: 0,
    borderBottom: `1px solid #E1E1E1`,
    marginLeft: 0,
    '&:not(:last-child)': {
      marginRight: 0,
    },
  },
  scrollButton: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#ffffff',
    width: 32,
    height: 32,
    borderRadius: 4,
    cursor: 'pointer',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      '& svg': {
        color: '#6C13F9 !important',
      },
    },
    '&.disabled': {
      opacity: 0,
      pointerEvents: 'none',
      transform: 'translateY(-50%)',
    },
  },
  scrollButtonIcon: {
    fontSize: 20,
    color: '#666666',
  },
}));

/**
 * Represents a single tab in the ScrollableTabs component
 */
export interface Tab {
  /** The content to display in the tab (can be text, JSX, or any React node) */
  label: React.ReactNode;
  /** Unique identifier for the tab, used for tracking active state */
  value: string;
  /** Optional CSS class name to apply custom styling to this specific tab */
  className?: string;
}

/**
 * Props for the ScrollableTabs component
 */
interface ScrollableTabsProps {
  /** Array of tabs to display in the scrollable area */
  tabs: Tab[];
  /** The value of the currently active tab */
  value: string;
  /** Callback fired when a tab is clicked - receives the event and new tab value */
  onChange: (event: React.SyntheticEvent, newValue: string) => void;
  /** Optional tab that stays fixed on the right side (doesn't scroll) */
  fixedTab?: Tab;
  /** Optional CSS class name to apply to the root container */
  className?: string;
}

/**
 * ScrollableTabs Component
 *
 * A Material-UI based horizontal tabs component with smooth scrolling capabilities.
 * Features include:
 * - Mouse wheel and trackpad scrolling support
 * - Left/right navigation buttons that appear/hide based on scroll position
 * - Optional fixed tab that remains visible on the right side
 * - Smooth CSS transitions and hover effects
 * - Responsive scroll amounts based on container size
 *
 * @param props - The component props
 * @returns A scrollable tabs component
 *
 * @example
 * ```tsx
 * const tabs = [
 *   { label: 'Orders', value: 'orders' },
 *   { label: 'Shipments', value: 'shipments' },
 *   { label: 'Inventory', value: 'inventory' }
 * ];
 *
 * const fixedTab = { label: 'Settings', value: 'settings' };
 *
 * <ScrollableTabs
 *   tabs={tabs}
 *   value={activeTab}
 *   onChange={(event, newValue) => setActiveTab(newValue)}
 *   fixedTab={fixedTab}
 * />
 * ```
 */
const ScrollableTabs: React.FC<ScrollableTabsProps> = ({
  tabs,
  value,
  onChange,
  fixedTab,
  className,
}) => {
  const classes = useStyles();

  // DOM references for scroll calculations and manipulation
  const tabsRef = useRef<HTMLDivElement>(null); // Container for scrollable tabs
  const wrapperRef = useRef<HTMLDivElement>(null); // Inner wrapper that gets transformed
  const fixedTabRef = useRef<HTMLDivElement>(null); // Reference to fixed tab for width calculation

  // State for controlling scroll button visibility and position
  const [showLeftButton, setShowLeftButton] = useState(false);
  const [showRightButton, setShowRightButton] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0); // Current transform X position
  const [fixedTabWidth, setFixedTabWidth] = useState(0); // Width of fixed tab for button positioning

  /**
   * Updates the visibility of scroll buttons based on current scroll position
   * and content overflow. Also updates the fixed tab width for proper button positioning.
   *
   * Left button shows when scrolled right from the start
   * Right button shows when there's more content to scroll to the right
   */
  const updateScrollButtons = useCallback(() => {
    if (!tabsRef.current || !wrapperRef.current) return;

    const container = tabsRef.current;
    const wrapper = wrapperRef.current;
    const containerWidth = container.offsetWidth;
    const wrapperWidth = wrapper.scrollWidth;
    const currentScroll = Math.abs(scrollPosition);

    // Update fixed tab width for proper right button positioning
    if (fixedTabRef.current) {
      setFixedTabWidth(fixedTabRef.current.offsetWidth);
    }

    setShowLeftButton(currentScroll > 0);
    setShowRightButton(currentScroll + containerWidth < wrapperWidth);
  }, [scrollPosition]);

  /**
   * Handles mouse wheel and trackpad scrolling events for horizontal scrolling.
   * Supports both vertical mouse wheel scrolling and horizontal trackpad scrolling.
   *
   * @param event - The wheel event from mouse or trackpad
   */
  const handleWheel = useCallback(
    (event: WheelEvent) => {
      if (!tabsRef.current || !wrapperRef.current) return;

      const container = tabsRef.current;
      const wrapper = wrapperRef.current;
      const containerWidth = container.offsetWidth;
      const wrapperWidth = wrapper.scrollWidth;

      // Only handle scrolling if content overflows
      if (wrapperWidth <= containerWidth) return;

      event.preventDefault();

      // Handle both vertical wheel (mouse) and horizontal trackpad scrolling
      const deltaX = event.deltaX;
      const deltaY = event.deltaY;

      // Use horizontal delta if available (trackpad), otherwise use vertical delta (mouse wheel)
      const scrollDelta = Math.abs(deltaX) > Math.abs(deltaY) ? deltaX : deltaY;

      // Scale the scroll amount for smoother experience
      const scrollAmount = scrollDelta * 0.5;

      let newPosition = scrollPosition - scrollAmount;

      // Clamp to boundaries - can't scroll past start (0) or end (maxScroll)
      const maxScroll = -(wrapperWidth - containerWidth);
      newPosition = Math.max(Math.min(newPosition, 0), maxScroll);

      setScrollPosition(newPosition);
    },
    [scrollPosition]
  );

  useEffect(() => {
    updateScrollButtons();

    const handleResize = () => updateScrollButtons();
    window.addEventListener('resize', handleResize);

    // Add wheel event listener to tabs container
    const tabsContainer = tabsRef.current;
    if (tabsContainer) {
      tabsContainer.addEventListener('wheel', handleWheel, { passive: false });
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      if (tabsContainer) {
        tabsContainer.removeEventListener('wheel', handleWheel);
      }
    };
  }, [updateScrollButtons, handleWheel]);

  /**
   * Scrolls the tabs in the specified direction using smooth animation.
   * Uses dynamic scroll amounts based on container size for better UX.
   *
   * @param direction - The direction to scroll ('left' or 'right')
   */
  const scroll = useCallback(
    (direction: 'left' | 'right') => {
      if (!tabsRef.current || !wrapperRef.current) return;

      const container = tabsRef.current;
      const wrapper = wrapperRef.current;
      const containerWidth = container.offsetWidth;
      const wrapperWidth = wrapper.scrollWidth;

      // Dynamic scroll amount based on container size, with min/max limits
      // Scroll about 70% of container width, but between 120px and 200px
      const baseScrollAmount = Math.min(containerWidth * 0.7, 200);
      const scrollAmount = Math.max(baseScrollAmount, 120);

      let newPosition = scrollPosition;

      if (direction === 'left') {
        // Scroll left means moving content right (positive direction)
        newPosition = Math.min(scrollPosition + scrollAmount, 0);
      } else {
        // Scroll right means moving content left (negative direction)
        const maxScroll = -(wrapperWidth - containerWidth);
        newPosition = Math.max(scrollPosition - scrollAmount, maxScroll);
      }

      // Ensure boundaries are respected
      if (newPosition > 0) newPosition = 0;
      if (newPosition < -(wrapperWidth - containerWidth)) {
        newPosition = -(wrapperWidth - containerWidth);
      }

      setScrollPosition(newPosition);
    },
    [scrollPosition]
  );

  /**
   * Creates a click handler for a specific tab.
   * Returns a function that calls the onChange callback with the tab's value.
   *
   * @param tab - The tab object to create a handler for
   * @returns A click event handler function
   */
  const handleTabClick = useCallback(
    (tab: Tab) => (event: React.MouseEvent) => {
      onChange(event, tab.value);
    },
    [onChange]
  );

  return (
    <Box className={`${classes.root} ${className || ''}`}>
      <div className={classes.tabsContainer} ref={tabsRef}>
        <div
          className={classes.tabsWrapper}
          ref={wrapperRef}
          style={{ transform: `translateX(${scrollPosition}px)` }}>
          {tabs.map((tab) => (
            <Box
              key={tab.value}
              className={`${classes.tab} ${value === tab.value ? classes.activeTab : ''} ${
                tab.className || ''
              }`}
              onClick={handleTabClick(tab)}>
              {tab.label}
            </Box>
          ))}
        </div>
      </div>

      {fixedTab && (
        <div
          ref={fixedTabRef}
          className={`${classes.tab} ${classes.fixedTab} ${
            value === fixedTab.value ? classes.activeTab : ''
          } ${fixedTab.className || ''}`}
          onClick={handleTabClick(fixedTab)}>
          {fixedTab.label}
        </div>
      )}

      <Box
        className={`${classes.scrollButton} ${!showLeftButton ? 'disabled' : ''}`}
        onClick={() => scroll('left')}>
        <ChevronLeftIcon className={classes.scrollButtonIcon} />
      </Box>

      <Box
        className={`${classes.scrollButton} ${!showRightButton ? 'disabled' : ''}`}
        onClick={() => scroll('right')}
        style={{ right: fixedTab ? fixedTabWidth : 8 }}>
        <ChevronRightIcon className={classes.scrollButtonIcon} />
      </Box>
    </Box>
  );
};

/**
 * Default export of the ScrollableTabs component.
 *
 * This component is commonly used throughout the Rollo Ship Manager application
 * for navigation between different sections like Orders, Shipments, Inventory, etc.
 *
 * Key features:
 * - Horizontal scrolling with mouse wheel and trackpad support
 * - Navigation buttons that auto-hide when not needed
 * - Fixed tab support for always-visible actions (like Settings)
 * - Material-UI theming integration
 * - Responsive design with dynamic scroll amounts
 * - Smooth CSS transitions and hover effects
 */
export default ScrollableTabs;
